// src/context/AppContext.js
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import apiService, { API_BASE_URL, updateApiClientBaseUrl } from '../services/api'; // Import API_BASE_URL correctly
import { useAuth } from './AuthContext'; // Import useAuth hook
import posthog from 'posthog-js';

// Create context
const AppContext = createContext();


// Helper to get initial state from localStorage or default
const getInitialState = (key, defaultValue) => {
  const storedValue = localStorage.getItem(key);
  try {
    return storedValue ? JSON.parse(storedValue) : defaultValue;
  } catch (e) {
    console.error(`Error parsing localStorage key "${key}":`, e);
    return defaultValue;
  }
};

// Helper to set state and update localStorage
const useStateWithLocalStorage = (key, defaultValue) => {
  const [state, setState] = useState(() => getInitialState(key, defaultValue));

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (e) {
      console.error(`Error setting localStorage key "${key}":`, e);
    }
  }, [key, state]);

  return [state, setState];
};


// Provider component
export const AppProvider = ({ children }) => {
  const navigate = useNavigate(); // Get navigate function
  const { user } = useAuth(); // Get user from AuthContext
  // Removed unused location variable

  // API Base URL - to be made available in context
  const apiBaseUrl = API_BASE_URL;

  // State for active page and tabs 
  // NOTE: activePage is primarily used by `loadProgramById` to track the previous page 
  // for back navigation logic (e.g., returning from Program Details to Sports Orgs).
  // It is NOT used for controlling which page component is rendered; that is handled by react-router-dom.
  const [activePage, setActivePage] = useState('programDetails'); 
  const [activeMainTab, setActiveMainTab] = useState('overview');
  const [activeSubTab, setActiveSubTab] = useState('summary');
  const [previousActivePage, setPreviousActivePage] = useState(null); // State to track the previous page
  const [sideMenuWidth, setSideMenuWidth] = useState(130);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false); 
  const [searchFilters, setSearchFilters] = useState({
    genre: '',
    network: '',
    yearFrom: '',
    yearTo: '',
    language: '',
    titleLanguage: 'all',
    descriptionLanguage: 'all',
    searchMode: 'startsWith'
  });
  
  // Sports page state
  const [sportsGenre, setSportsGenre] = useState(null);
  const [sportsOrg, setSportsOrg] = useState(null);
  const [sportsConnector, setSportsConnector] = useState(null);
  const [sportsSeason, setSportsSeason] = useState(null);
  const [cameFromSportsPage, setCameFromSportsPage] = useState(false);

  // Main Search Results Page State
  const [mainSearchResultsPage, setMainSearchResultsPage] = useState(1); // Add state for current page
  const [mainSearchResultsPageSize, setMainSearchResultsPageSize] = useState(10); // Add state for page size (default 10)
  const [mainSearchResultsSelected, setMainSearchResultsSelected] = useState([]); // Add state for selections

  // Compare Programs State
  const [comparisonTmsIds, setComparisonTmsIds] = useState(null);

  // --- Lineups Page State (using localStorage persistence where appropriate) ---
  const [lineupsPostalCode, setLineupsPostalCode] = useStateWithLocalStorage('lineupsPostalCode', '');
  const [lineupsMsoInput, setLineupsMsoInput] = useStateWithLocalStorage('lineupsMsoInput', '');
  const [lineupsList, setLineupsList] = useState([]); // List of potential lineups from search
  const [lineupsSelectedLineup, setLineupsSelectedLineup] = useStateWithLocalStorage('lineupsSelectedLineup', null);
  const [lineupsChannels, setLineupsChannels] = useState([]);
  const [lineupsSelectedChannel, setLineupsSelectedChannel] = useStateWithLocalStorage('lineupsSelectedChannel', null);
  const [lineupsScheduleDates, setLineupsScheduleDates] = useState(null); // Initialized to null
  const [lineupsSelectedDate, setLineupsSelectedDate] = useStateWithLocalStorage('lineupsSelectedDate', null); // Store the whole scheduleDate object
  const [lineupsSchedulePrograms, setLineupsSchedulePrograms] = useState(null);
  const [lineupsChannelFilter, setLineupsChannelFilter] = useStateWithLocalStorage('lineupsChannelFilter', '');
  const [lineupsIsLoading, setLineupsIsLoading] = useState({ lineups: false, channels: false, dates: false, programs: false });
  const [lineupsError, setLineupsError] = useState({ lineups: null, channels: null, dates: null, programs: null });
  const [lineupSortField, setLineupSortField] = useStateWithLocalStorage('lineupSortField', 'lineupId');
  const [lineupSortDirection, setLineupSortDirection] = useStateWithLocalStorage('lineupSortDirection', 'asc');
  const [channelSortField, setChannelSortField] = useStateWithLocalStorage('channelSortField', 'channelNumber');
  const [channelSortDirection, setChannelSortDirection] = useStateWithLocalStorage('channelSortDirection', 'asc');
  // --- End Lineups Page State ---

  // Settings Modal State
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isMyAccountModalOpen, setIsMyAccountModalOpen] = useState(false);
  // const [language, setLanguage] = useState('en'); // Add language state - REPLACED
  const [language, setLanguage] = useStateWithLocalStorage('userLanguage', 'en'); // Use localStorage, default 'en'
  const [contentLanguage, setContentLanguage] = useStateWithLocalStorage('contentLanguage', 'all');
  const [translations, setTranslations] = useState({}); // State for loaded translations

  // Initialize ignoreAdultContent from localStorage, default to true
  const [ignoreAdultContent, setIgnoreAdultContent] = useState(() => {
    const storedValue = localStorage.getItem('ignoreAdultContent');
    // Default to true if not found or invalid
    return storedValue !== null ? JSON.parse(storedValue) : true;
  });
  // Initialize favouriteSports from localStorage, default to empty string
  const [favouriteSports, setFavouriteSports] = useStateWithLocalStorage('favouriteSports', ''); // Use helper
  // Add state for Debug Mode using the helper
  const [isDebugMode, setIsDebugMode] = useStateWithLocalStorage('isDebugMode', false);
  
  // Add state for Performance Tracking using the helper (default to true)
  const [enablePerformanceTracking, setEnablePerformanceTracking] = useStateWithLocalStorage('enablePerformanceTracking', true);

  // --- Saved Programs State ---
  const [savedProgramTmsIds, setSavedProgramTmsIds] = useStateWithLocalStorage('savedProgramTmsIds', []);

  // --- Timezone State ---
  const [userTimezoneId, setUserTimezoneId] = useStateWithLocalStorage('userTimezoneId', 0); // Default to UTC (id: 0)
  const [availableTimezones, setAvailableTimezones] = useState([]);


  // --- Settings Modal Functions ---
  const openSettingsModal = useCallback(() => {
    setIsSettingsModalOpen(true);
  }, []);

  const closeSettingsModal = useCallback(() => {
    setIsSettingsModalOpen(false);
  }, []);

  const openMyAccountModal = useCallback(() => {
    setIsMyAccountModalOpen(true);
  }, []);

  const closeMyAccountModal = useCallback(() => {
    setIsMyAccountModalOpen(false);
  }, []);


  const handleLanguageChange = useCallback((lang) => {
    setLanguage(lang);
    if (user) {
      apiService.saveUserSetting(user.user_id, 'userLanguage', lang);
    }
    console.log("Language changed to:", lang);
  }, [setLanguage, user]);

  const handleContentLanguageChange = useCallback((lang) => {
    setContentLanguage(lang);
    if (user) {
      apiService.saveUserSetting(user.user_id, 'contentLanguage', lang);
    }
    setSearchFilters(prevFilters => ({
        ...prevFilters,
        descriptionLanguage: lang
    }));
    console.log("Content language changed to:", lang);
  }, [setContentLanguage, user, setSearchFilters]);


  const handleIgnoreAdultContentChange = useCallback((value) => {
    setIgnoreAdultContent(value);
    localStorage.setItem('ignoreAdultContent', JSON.stringify(value)); // Save to localStorage
    console.log("Ignore Adult Content set to:", value);
  }, []);

  const handleFavouriteSportsChange = useCallback((value) => {
    setFavouriteSports(value);
    localStorage.setItem('favouriteSports', value);
    console.log("Favourite Sports set to:", value);
  }, [setFavouriteSports]);

  // Handler for Debug Mode change
  const handleDebugModeChange = useCallback((value) => {
    setIsDebugMode(Boolean(value)); // Ensure it's stored as boolean
    console.log("Debug Mode set to:", Boolean(value));
  }, [setIsDebugMode]);

  // Handler for Performance Tracking change
  const handlePerformanceTrackingChange = useCallback((value) => {
    const enabled = Boolean(value);
    setEnablePerformanceTracking(enabled); // Ensure it's stored as boolean
    try {
      localStorage.setItem('enablePerformanceTracking', JSON.stringify(enabled));
    } catch (e) {
      console.warn('Failed to persist enablePerformanceTracking:', e);
    }
    // Apply to PostHog immediately
    try {
      if (enabled) {
        if (posthog.__loaded) {
          posthog.opt_in_capturing();
        } else {
          // Initialize on-the-fly to start capturing only when enabled
          posthog.init('phc_uPKPi3YhmQCgfDeQOVeDnALHjoThjzlG5R3EFtcoO2o', {
            api_host: 'https://app.posthog.com',
            capture_pageview: true,
            capture_pageleave: true,
          });
        }
      } else {
        if (posthog.__loaded) {
          posthog.opt_out_capturing();
        }
      }
    } catch (phErr) {
      console.warn('PostHog toggle failed:', phErr);
    }
    console.log("Performance Tracking set to:", enabled);
  }, [setEnablePerformanceTracking]);

  // Handler for Timezone change
  const handleTimezoneChange = useCallback((timezoneId) => {
    setUserTimezoneId(Number(timezoneId));
    if (user) {
      apiService.saveUserSetting(user.user_id, 'userTimezoneId', String(timezoneId));
    }
    console.log("Timezone changed to ID:", timezoneId);
  }, [setUserTimezoneId, user]);

  // Fetch available timezones on mount
  useEffect(() => {
    const fetchTimezones = async () => {
      try {
        const response = await apiService.getTimezones();
        setAvailableTimezones(response.data || []);
      } catch (error) {
        console.error('Failed to fetch timezones:', error);
        // Set a default UTC timezone if fetch fails
        setAvailableTimezones([{ id: 0, name: 'UTC', iana_name: 'UTC', offset: '+00:00' }]);
      }
    };
    fetchTimezones();
  }, []);


  // --- Localization ---
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        // Ensure language is a simple string like 'en' or 'fr'
        const langToLoad = typeof language === 'string' ? language.split('-')[0].toLowerCase() : 'en';
        const module = await import(`../locales/${langToLoad}.json`);
        setTranslations(module); // Directly use the imported module for JSON
        console.log(`Translations loaded for ${langToLoad}:`, module); // DEBUG
      } catch (error) {
        console.error(`Could not load translations for ${language}:`, error);
        // Fallback to English if the selected language file fails to load
        try {
          const fallbackModule = await import(`../locales/en.json`);
          setTranslations(fallbackModule); // Directly use the imported module for JSON
          console.log(`Fallback English translations loaded:`, fallbackModule); // DEBUG
        } catch (fallbackError) {
          console.error(`Could not load fallback English translations:`, fallbackError);
          setTranslations({}); // Empty if fallback also fails
        }
      }
    };
    loadTranslations();
  }, [language]); // Reload when language changes

  const t = useCallback((key, options) => {
    // console.log(`Attempting to translate key: "${key}" with options:`, options, "and translations:", translations); // DEBUG
    // Directly access the key from the flat translations object
    let translation = translations?.[key];

    if (translation === undefined) {
      // console.warn(`Translation key "${key}" not found.`);
      return key; // Return the key itself if not found
    }

    if (typeof translation === 'string' && options) {
      // Perform interpolation
      Object.keys(options).forEach(optionKey => {
        const regex = new RegExp(`{${optionKey}}`, 'g');
        translation = translation.replace(regex, options[optionKey]);
      });
    }
    
    return typeof translation === 'string' ? translation : key; // Ensure it's a string
  }, [translations]);
  // --- End Localization ---

  // --- End Settings Modal Functions ---

  // --- Saved Programs Functions ---
  const toggleSaveProgram = useCallback(async (tmsId) => {
    const isCurrentlySaved = savedProgramTmsIds.includes(tmsId);
    const currentUserId = user?.user_id; // Get current userId from user object

    if (!currentUserId) {
      console.error("Cannot save program: user is not authenticated or userId is not available.");
      // Optionally, notify user to sign in
      return;
    }

    // Optimistically update UI state
    setSavedProgramTmsIds(prevTmsIds => {
      if (isCurrentlySaved) {
        return prevTmsIds.filter(id => id !== tmsId);
      } else {
        return [...prevTmsIds, tmsId];
      }
    });

    try {
      if (isCurrentlySaved) {
        // Action is to remove
        await apiService.deleteSavedItem(currentUserId, tmsId);
        console.log(`Successfully removed program ${tmsId} for user ${currentUserId} via API.`);
      } else {
        // Action is to add
        await apiService.saveItem(currentUserId, tmsId);
        console.log(`Successfully added program ${tmsId} for user ${currentUserId} via API.`);
      }
    } catch (error) {
      // If deleting and it's a 404, it means the item was already gone from the backend.
      // In this case, the optimistic UI update (removal) is correct, so we can silently ignore.
      if (isCurrentlySaved && error.response && error.response.status === 404) {
        console.log(`Program ${tmsId} was not found on backend for deletion (already removed), UI state is correct.`);
      } else {
        // For other errors, or errors during an "add" operation, log and revert.
        console.error(`Failed to update saved status for program ${tmsId} (user ${currentUserId}) via API:`, error);
        // Revert UI state on API error
        setSavedProgramTmsIds(prevTmsIds => {
          if (isCurrentlySaved) { // Original action was remove, failed (not 404), so add back
            return [...prevTmsIds, tmsId];
          } else { // Original action was add, failed, so remove
            return prevTmsIds.filter(id => id !== tmsId);
          }
        });
        // Optionally, notify user of the error
      }
    }
  }, [savedProgramTmsIds, setSavedProgramTmsIds, user]); // Added user to dependencies
  // --- End Saved Programs Functions ---

  // Fetch and apply user settings on login
  useEffect(() => {
    const fetchUserSettings = async () => {
      if (user && user.user_id) {
        try {
          const settings = await apiService.getUserSettings(user.user_id);
          if (settings && settings.contentLanguage) {
            setContentLanguage(settings.contentLanguage);
            setSearchFilters(prevFilters => ({
              ...prevFilters,
              descriptionLanguage: settings.contentLanguage
            }));
          }
        } catch (error) {
          console.error("Failed to fetch user settings:", error);
        }
      }
    };

    fetchUserSettings();
  }, [user, setContentLanguage, setSearchFilters]);


  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (Object.values(searchFilters).some(val => val !== '')) {
      setShowSearchResults(true);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchFilters({
      genre: '',
      network: '',
      yearFrom: '',
      yearTo: '',
      language: '',
      titleLanguage: 'all',
      descriptionLanguage: contentLanguage
    });
  };

  // Handle main tab change
  const handleMainTabChange = (tabId) => {
    setActiveMainTab(tabId);
    // Reset subtab to summary if we're switching to overview
    if (tabId === 'overview') {
      setActiveSubTab('summary');
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle result selection from real-time search
  const handleResultSelection = useCallback((result) => {
    // The result is already formatted in RealTimeSearch component
    setPreviousActivePage(activePage); // Store the current page before navigating
    setSelectedResult(result);
    setActivePage('programDetails');
  }, [activePage]); // Add activePage dependency

  // Load a program by its TMS ID
  const loadProgramById = useCallback(async (tmsId, fromSportsPage = false) => {
    setIsLoading(true);
    try {
      // Store the current page before navigating, unless it's already programDetails
      if (activePage !== 'programDetails') {
        setPreviousActivePage(activePage);
      }
      
      // Set active tab to overview and subtab to summary
      setActiveMainTab('overview');
      setActiveSubTab('summary');

      // Create a minimal result object with just the ID to trigger initial data loading
      setSelectedResult({
        id: tmsId,
        title: 'Loading...',
        description: 'Loading program details...',
        genre: '',
        network: '',
        year: ''
      });

      // setActivePage('programDetails'); // No longer needed for routing
      setCameFromSportsPage(fromSportsPage);
      
      // --- Navigate to the program details page ---
      navigate(`/program/${tmsId}`); 
      // --- End Navigation ---

      // Fetch overview data to enhance the program information
      // Note: Related programs will be loaded asynchronously in the ProgramSummary component
      try {
        const overviewData = await apiService.getOverview(tmsId);
        
        // Get title from either the overview directly or by searching
        let title = overviewData.title || '';
        
        // If title is not available, try searching for it
        if (!title) {
          try {
            // When searching by TMS ID, we don't need to filter by program type
            const searchResults = await apiService.search(tmsId, null);
            if (searchResults && searchResults.length > 0) {
              // Find exact match by TMSId
              const exactMatch = searchResults.find(r => r.TMSId === tmsId);
              if (exactMatch) {
                title = exactMatch.title;
              }
            }
          } catch (searchError) {
            console.error('Error searching for program:', searchError);
          }
        }
        
        // Extract year from mvinfo
        let year = '';
        if (overviewData.mvinfo) {
          const yearInfo = overviewData.mvinfo.find(item => item.key === 'yearOfRelease');
          if (yearInfo) {
            year = yearInfo.value;
          }
        }
        
        // Extract genres as a string
        let genres = '';
        if (overviewData.genres && overviewData.genres.length > 0) {
          genres = overviewData.genres
            .sort((a, b) => a.genreOrd - b.genreOrd)
            .map(g => g.genre)
            .join(', ');
        }
        
        // Update selected result with more complete information
        setSelectedResult(prev => ({
          ...prev,
          title: title || 'Unknown Title',
          year: year,
          genre: genres,
          id: tmsId
        }));
      } catch (error) {
        console.error('Error fetching overview data:', error);
      }
    } catch (err) {
      console.error('Failed to load program:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activePage, navigate]); // Add navigate dependency

  // Function to navigate to the main search results page (e.g., from header search)
  const navigateToMainSearchResults = useCallback((term, type, filters) => {
    const params = new URLSearchParams();
    if (term) {
      params.append('term', term);
    }
    if (type && type !== 'ALL') {
      params.append('type', type);
    }
    if (filters.yearFrom) {
      params.append('yearFrom', filters.yearFrom);
    }
    if (filters.yearTo) {
      params.append('yearTo', filters.yearTo);
    }
    if (filters.titleLanguage && filters.titleLanguage !== 'all') {
      params.append('titleLang', filters.titleLanguage);
    }
    if (filters.descriptionLanguage && filters.descriptionLanguage !== 'all') {
      params.append('descLang', filters.descriptionLanguage);
    }
    // Note: genre and network filters are not currently passed from RealTimeSearch,
    // but if they were, they would be added here.

    setMainSearchResultsPage(1); // Reset to page 1 for new search
    setMainSearchResultsSelected([]); // Clear selections for new search
    navigate(`/results?${params.toString()}`); // Navigate with query parameters
    setSelectedResult(null); // Clear program details view if active
  }, [navigate]); // Added navigate dependency

  // Function to navigate to the Compare Programs page
  const navigateToComparePrograms = useCallback((tmsids) => {
    // No need to clear mainSearchResultsSelected here, preserve it
    if (tmsids && typeof tmsids === 'string') {
      setComparisonTmsIds(tmsids);
      // setActivePage('comparePrograms'); // REMOVED: No longer used for routing
      navigate(`/compare?ids=${tmsids}`, {
        state: {
          fromSearchResults: true,
        }
      }); // Pass state to indicate origin and search context
      // Don't clear setSelectedResult here either, might be unrelated
      console.log(`Navigating to compare programs with TMS IDs: ${tmsids}`);
    } else {
      console.error("Invalid TMS IDs provided for comparison:", tmsids);
    }
  }, [navigate]); // navigate is the only dependency now

  // Function to reset sports-related state
  const resetSportsState = useCallback(() => {
    setSportsGenre(null);
    setSportsOrg(null);
    setSportsConnector(null);
    setSportsSeason(null);
    // setCameFromSportsPage(false); // Optionally reset this too if needed
    console.log("Sports state reset");
  }, []);

  // --- Lineups Helper Functions (Define before use) ---
  const setLineupsLoading = useCallback((key, value) => {
    setLineupsIsLoading(prev => ({ ...prev, [key]: value }));
  }, []); // Depends only on base state setter

  const setLineupsErr = useCallback((key, value) => {
    setLineupsError(prev => ({ ...prev, [key]: value }));
  }, []); // Depends only on base state setter

  // --- Lineups Main Functions ---

  // Function to fetch lineups by postal code
  const fetchLineupsByPostal = useCallback(async () => {
    // Use the postal code from state directly
    if (!lineupsPostalCode || lineupsPostalCode.length < 3) {
      setLineupsList([]); // Clear list if input is too short or empty
      // Optionally clear error if input becomes valid/empty
      // setLineupsError(prev => ({ ...prev, lineups: null }));
      return;
    }
    setLineupsLoading('lineups', true); setLineupsErr('lineups', null);
    setLineupsList([]); // Clear previous results before fetching
    try {
      // apiService.getLineups returns response.data
      const apiResult = await apiService.getLineups(lineupsPostalCode);
      console.log("API Result from getLineups:", apiResult);
      
      // Ensure the result is an array before setting state
      // If apiResult is already an array, use it directly
      const finalLineups = Array.isArray(apiResult) ? apiResult : 
                          (apiResult && apiResult.data && Array.isArray(apiResult.data)) ? apiResult.data : [];
      console.log("Final lineups to set:", finalLineups);
      
      setLineupsList(finalLineups);
    } catch (err) {
      console.error('Failed to fetch lineups by postal:', err);
      setLineupsErr('lineups', err.message || 'Failed to load lineups.');
      setLineupsList([]); // Ensure list is empty on error
    } finally {
      setLineupsLoading('lineups', false);
    }
  }, [lineupsPostalCode, setLineupsLoading, setLineupsErr, setLineupsList]); // Added dependencies


  // Note: fetchLineupsByPostal defined above.

  const fetchLineupsByMso = useCallback(async (mso) => {
    if (!mso || mso.length < 3) {
      setLineupsList([]);
      return;
    }
    setLineupsLoading('lineups', true); setLineupsErr('lineups', null);
    setLineupsList([]); // Clear previous results before fetching
    try {
      // apiService.getLineupsByMso returns response.data which may have a nested data property
      const apiResult = await apiService.getLineupsByMso(mso);
      // Check if the result has a data property (nested structure)
      const lineups = apiResult && apiResult.data ? apiResult.data : apiResult;
      // Ensure the result is an array before setting state
      setLineupsList(Array.isArray(lineups) ? lineups : []);
    } catch (err) {
      console.error('Failed to fetch lineups by MSO:', err);
      setLineupsErr('lineups', err.message || 'Failed to load lineups.');
      setLineupsList([]); // Clear list on error
    } finally {
      setLineupsLoading('lineups', false);
    }
  }, [setLineupsLoading, setLineupsErr]); // Added dependencies

  const selectLineup = useCallback(async (lineup) => {
    // Clear subsequent state first
    setLineupsChannels([]);
    setLineupsSelectedChannel(null);
    setLineupsScheduleDates(null); // Set to null
    setLineupsSelectedDate(null);
    setLineupsSchedulePrograms(null);
    setLineupsChannelFilter(''); // Reset channel filter on new lineup selection

    setLineupsSelectedLineup(lineup); // Set the selected lineup

    if (!lineup) return; // If deselecting, stop here

    setLineupsLoading('channels', true); setLineupsErr('channels', null);
    try {
      const response = await apiService.getLineupDetails(lineup.lineupId);
      // Use response directly if it's the array, otherwise check response.data
      const data = Array.isArray(response) ? response : (response?.data || []);
      setLineupsChannels(data);
    } catch (err) {
      console.error('Failed to fetch lineup details:', err);
      setLineupsErr('channels', 'Failed to load channels.');
      setLineupsChannels([]); // Clear channels on error
    } finally {
      setLineupsLoading('channels', false);
    }
  }, [setLineupsLoading, setLineupsErr, setLineupsChannelFilter, setLineupsSelectedChannel, setLineupsSelectedDate, setLineupsSelectedLineup, setLineupsChannels, setLineupsScheduleDates, setLineupsSchedulePrograms]); // Added more dependencies based on usage

   const selectChannel = useCallback(async (channel) => {
     // Clear subsequent state first
    setLineupsScheduleDates(null); // Set to null
    setLineupsSelectedDate(null);
    setLineupsSchedulePrograms(null);

    setLineupsSelectedChannel(channel); // Set selected channel

    if (!channel) return; // If deselecting, stop here

    setLineupsLoading('dates', true); setLineupsErr('dates', null);
    try {
      const response = await apiService.getSchedulesByPrgSvcId(channel.prgSvcId, userTimezoneId);
      // Use response directly if it's the array, otherwise check response.data
      const data = Array.isArray(response) ? response : (response?.data || []);
      setLineupsScheduleDates(data);
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
      setLineupsErr('dates', 'Failed to load schedules.');
      setLineupsScheduleDates([]); // Clear dates on error
    } finally {
      setLineupsLoading('dates', false);
    }
  }, [setLineupsLoading, setLineupsErr, setLineupsSelectedDate, setLineupsScheduleDates, setLineupsSchedulePrograms, setLineupsSelectedChannel]); // Added more dependencies

  const selectDate = useCallback(async (scheduleDate) => { // scheduleDate is the full object { scheduleId, date }
    // Clear subsequent state first
    setLineupsSchedulePrograms(null);

    setLineupsSelectedDate(scheduleDate); // Set selected date object

    if (!scheduleDate || !scheduleDate.scheduleId) return; // If deselecting or invalid, stop here

    setLineupsLoading('programs', true); setLineupsErr('programs', null);
    try {
      const response = await apiService.getScheduleDetails(scheduleDate.scheduleId, userTimezoneId);
      // Use response directly if it's the array, otherwise check response.data
      const data = Array.isArray(response) ? response : (response?.data || []);
      setLineupsSchedulePrograms(data);
    } catch (err) {
      console.error('Failed to fetch schedule details:', err);
      setLineupsErr('programs', 'Failed to load programs.');
      setLineupsSchedulePrograms([]); // Clear programs on error
    } finally {
      setLineupsLoading('programs', false);
    }
  }, [setLineupsLoading, setLineupsErr, setLineupsSchedulePrograms, setLineupsSelectedDate, userTimezoneId]); // Added userTimezoneId dependency

  // Function to load lineup state based on URL parameters
  const loadLineupStateFromParams = useCallback(async (params) => {
    const { lineupId, channelId, scheduleId } = params;
    console.log("Loading lineup state from params:", params);

    // Reset relevant parts of state before loading
    setLineupsList([]); // Clear search list
    setLineupsChannels([]);
    setLineupsScheduleDates(null); // Set to null
    setLineupsSchedulePrograms(null);
    // Reset errors, but let individual loading flags be managed by their respective fetch sequences.
    // The overall 'lineups' loading flag will gate the entire loadLineupStateFromParams operation.
    setLineupsError({ lineups: null, channels: null, dates: null, programs: null });

    if (!lineupId) {
      setLineupsSelectedLineup(null);
      setLineupsSelectedChannel(null);
      setLineupsSelectedDate(null);
      return;
    }

    setLineupsLoading('lineups', true); // Overall loading for this function starts
    try {
      // Fetch *specific* lineup details if we don't have it or it's different
      // We might need an endpoint like getLineupById(lineupId) or adapt getLineupsByMso
      // For now, assume we might need to fetch channels regardless
      // Let's simulate finding the lineup if it was previously searched (less ideal)
      // A dedicated getLineupById endpoint would be better.
      // Setting a placeholder lineup object for now.
      const placeholderLineup = { lineupId: lineupId, headend: `Lineup ${lineupId}`, /* other fields potentially missing */ };
      setLineupsSelectedLineup(placeholderLineup); // Set selected lineup early

      // --- Load Channels ---
      setLineupsLoading('channels', true); setLineupsErr('channels', null);
      const channelResponse = await apiService.getLineupDetails(lineupId);
      // Use response directly if it's the array, otherwise check response.data
      const channelData = Array.isArray(channelResponse) ? channelResponse : (channelResponse?.data || []);
      setLineupsChannels(channelData);
      setLineupsLoading('channels', false);

      if (!channelId || !channelData.length) {
        setLineupsSelectedChannel(null);
        setLineupsSelectedDate(null);
        return; // Stop if no channelId or no channels found
      }

      // --- Find and Select Channel ---
      const foundChannel = channelData.find(c => String(c.prgSvcId) === channelId || String(c.channelId) === channelId); // Check prgSvcId or channelId
      if (!foundChannel) {
        setLineupsErr('channels', `Channel ID ${channelId} not found in lineup ${lineupId}.`);
        setLineupsSelectedChannel(null);
        setLineupsSelectedDate(null);
        return; // Stop if channel not found
      }
      setLineupsSelectedChannel(foundChannel); // Set selected channel

      // --- Load Schedule Dates ---
      setLineupsLoading('dates', true); setLineupsErr('dates', null);
      const scheduleResponse = await apiService.getSchedulesByPrgSvcId(foundChannel.prgSvcId, userTimezoneId);
      // Use response directly if it's the array, otherwise check response.data
      const scheduleData = Array.isArray(scheduleResponse) ? scheduleResponse : (scheduleResponse?.data || []);
      setLineupsScheduleDates(scheduleData);
      setLineupsLoading('dates', false);

      if (!scheduleId || !scheduleData.length) {
        setLineupsSelectedDate(null);
        return; // Stop if no scheduleId or no dates found
      }

      // --- Find and Select Date ---
      const foundScheduleDate = scheduleData.find(d => String(d.scheduleId) === scheduleId);
      if (!foundScheduleDate) {
        setLineupsErr('dates', `Schedule ID ${scheduleId} not found for channel ${channelId}.`);
        setLineupsSelectedDate(null);
        return; // Stop if schedule date not found
      }
      setLineupsSelectedDate(foundScheduleDate); // Set selected date object

      // --- Load Schedule Programs ---
      setLineupsLoading('programs', true); setLineupsErr('programs', null);
      const programResponse = await apiService.getScheduleDetails(foundScheduleDate.scheduleId, userTimezoneId);
      // Use response directly if it's the array, otherwise check response.data
      const programData = Array.isArray(programResponse) ? programResponse : (programResponse?.data || []);
      setLineupsSchedulePrograms(programData);
      setLineupsLoading('programs', false);
      setLineupsLoading('lineups', false); // Overall loading for this function ends successfully

    } catch (err) {
      console.error('Error loading lineup state from params:', err);
      setLineupsErr('lineups', 'Failed to load lineup state from URL.');
      // Reset selections on major error
      setLineupsSelectedLineup(null);
      setLineupsSelectedChannel(null);
      setLineupsSelectedDate(null);
      // Ensure all loading flags are false on error
      setLineupsIsLoading({ lineups: false, channels: false, dates: false, programs: false });
    }
  }, [ /* Add necessary setters like setLineupsSelectedLineup etc. */
      setLineupsSelectedLineup, setLineupsSelectedChannel, setLineupsSelectedDate,
      setLineupsChannels, setLineupsScheduleDates, setLineupsSchedulePrograms,
      setLineupsLoading, setLineupsErr, setLineupsList, setLineupsIsLoading, setLineupsError // Added missing setters
  ]);


  const resetLineupsContextState = useCallback((keepInputs = false) => {
    if (!keepInputs) {
      setLineupsPostalCode('');
      setLineupsMsoInput('');
    }
    setLineupsList([]);
    setLineupsSelectedLineup(null);
    setLineupsChannels([]);
    setLineupsSelectedChannel(null);
    setLineupsScheduleDates(null); // Set to null
    setLineupsSelectedDate(null);
    setLineupsSchedulePrograms(null);
    setLineupsChannelFilter('');
    setLineupsIsLoading({ lineups: false, channels: false, dates: false, programs: false });
    setLineupsError({ lineups: null, channels: null, dates: null, programs: null });
    // Optionally reset sorting/filtering or keep them
    // setLineupSortField('lineupId');
    // setLineupSortDirection('asc');
    // setChannelSortField('channelNumber');
    // setChannelSortDirection('asc');
    console.log("Lineups context state reset.");
    // Navigate to base lineups page when resetting fully
    if (!keepInputs) {
       navigate('/lineups');
    }
  }, [
    setLineupsPostalCode, setLineupsMsoInput, setLineupsList, setLineupsSelectedLineup,
    setLineupsChannels, setLineupsSelectedChannel, setLineupsScheduleDates,
    setLineupsSelectedDate, setLineupsSchedulePrograms, setLineupsChannelFilter,
    setLineupsIsLoading, setLineupsError, navigate // Added navigate
  ]);
  // --- End Lineups Functions ---


  return (
    <AppContext.Provider
      value={{
        activePage, // Keep for previousActivePage logic
        // setActivePage, // REMOVED: Use navigate for routing changes
        activeMainTab,
        setActiveMainTab: handleMainTabChange,
        activeSubTab,
        setActiveSubTab,
        previousActivePage, // Export previous page state
        sideMenuWidth,
        setSideMenuWidth,
        showSearchResults,
        setShowSearchResults,
        selectedResult,
        setSelectedResult: handleResultSelection,
        advancedSearch,
        setAdvancedSearch,
        searchFilters,
        setSearchFilters,
        handleSearch,
        clearSearch,
        handleFilterChange,
        isLoading,
        loadProgramById,
        // Sports page state
        sportsGenre,
        setSportsGenre,
        sportsOrg,
        setSportsOrg,
        sportsConnector,
        setSportsConnector,
        sportsSeason,
        setSportsSeason,
        cameFromSportsPage,
        setCameFromSportsPage,
        resetSportsState, // Export the reset function
        // Settings Modal
        isSettingsModalOpen,
        openSettingsModal,
        closeSettingsModal,
        isMyAccountModalOpen,
        openMyAccountModal,
        closeMyAccountModal,
        language, // Export language state
        handleLanguageChange, // Export language handler
        contentLanguage,
        handleContentLanguageChange,
        ignoreAdultContent, // Export ignore adult content state
        handleIgnoreAdultContentChange, // Export ignore adult content handler
        favouriteSports, // Export favourite sports state
        handleFavouriteSportsChange, // Export favourite sports handler
        isDebugMode, // Export debug mode state
        handleDebugModeChange, // Export debug mode handler
        enablePerformanceTracking, // Export performance tracking state
        handlePerformanceTrackingChange, // Export performance tracking handler
        t, // Export translation function
        // Main Search Results
        navigateToMainSearchResults,
        mainSearchResultsPage,      // Export page state
        setMainSearchResultsPage,   // Export page setter
        mainSearchResultsPageSize,  // Export page size state
        setMainSearchResultsPageSize, // Export page size setter
        mainSearchResultsSelected,  // Export selections state
        setMainSearchResultsSelected, // Export selections setter
        // Compare Programs
        comparisonTmsIds,
        setComparisonTmsIds, // Expose the setter function
        navigateToComparePrograms,
        apiBaseUrl, // Expose API_BASE_URL
        // Lineups State & Functions
        lineupsPostalCode, setLineupsPostalCode,
        lineupsMsoInput, setLineupsMsoInput,
        lineupsList, // Export the list of found lineups
        lineupsSelectedLineup, // No direct setter, use selectLineup
        lineupsChannels, // No direct setter
        lineupsSelectedChannel, // No direct setter, use selectChannel
        lineupsScheduleDates, // No direct setter
        lineupsSelectedDate, // No direct setter, use selectDate
        lineupsSchedulePrograms, // No direct setter
        lineupsChannelFilter, setLineupsChannelFilter,
        lineupsIsLoading, // Export loading state object
        lineupsError, // Export error state object
        lineupSortField, setLineupSortField,
        lineupSortDirection, setLineupSortDirection,
        channelSortField, setChannelSortField,
        channelSortDirection, setChannelSortDirection,
        fetchLineupsByPostal, // Function to fetch by postal
        fetchLineupsByMso, // Function to fetch by MSO
        selectLineup, // Function to select a lineup and fetch channels
        selectChannel, // Function to select a channel and fetch dates
        selectDate, // Function to select a date and fetch programs
        resetLineupsContextState, // Function to clear the context state
        loadLineupStateFromParams, // Function to load state from URL
        // Saved Programs
        savedProgramTmsIds,
        toggleSaveProgram,
        // Timezone
        userTimezoneId,
        availableTimezones,
        handleTimezoneChange
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = () => useContext(AppContext);

export default AppContext;
<environment_details>
# Visual Studio Code Visible Files
viewer/src/context/AppContext.js

# Visual Studio Code Open Tabs
backend/routes/genres.py
viewer/src/components/program/ProgramAiringData.jsx
create_panorama_database.sql
PANORAMA_DATABASE_SETUP.md
panorama_database_schema.sql
create_backend_tables.sql
viewer/src/components/layout/Footer.jsx
viewer/src/pages/AdminUsersPage.jsx
deploy.bat
deployprod.bat
backend/routes/episodes.py
viewer/src/App.css
viewer/.env.example
QUICK_SETUP_INSTRUCTIONS.md
create_saved_items_table.sql
d:/application.properties
viewer/src/components/common/AssetTypeFilterModal.jsx
backend/routes/telus.py
viewer/src/components/common/TitleSearchModal.jsx
backend/create_encore_product_vectors.py
backend/create_encore_lancedb.py
backend/get_lancedb_recos.py
viewer/src/components/common/GenreFilterModal.jsx
viewer/.env.preprod
backend/routes/__init__.py
backend/BACKENDS_API_DOCUMENTATION.md
backend/auth_decorators.py
backend/BACKEND_authorization.md
viewer/src/pages/AdminBackendsPage.jsx
backend/routes/backends.py
viewer/src/components/search/RealTimeSearch.jsx
viewer/src/locales/fr.json
viewer/src/pages/CelebritiesPage.jsx
viewer/src/locales/en.json
viewer/src/data/navData.js
viewer/src/components/layout/Sidebar.jsx
viewer/src/components/auth/ProtectedRoute.jsx
viewer/src/components/layout/Header.jsx
backend/routes/auth.py
backend/app.py
viewer/src/pages/MainSearchResults.jsx
viewer/src/config.js
viewer/src/context/AppContext.js

# Recently Modified Files
These files have been modified since you last accessed them (file was just edited so you may need to re-read it before editing):
viewer/src/context/AppContext.js

# Current Time
11/20/2025, 2:48:09 PM (America/Halifax, UTC-4:00)

# Context Window Usage
291,476 / 1,048.576K tokens used (27%)

# Current Mode
ACT MODE
</environment_details>
